import { api } from "../../../data";
import type { SenderDomainRecord } from "../../../data/api";
import { PillView, useAction } from "../../../components/ui";
import { toast } from "../../../components/Toast";
import { date, time } from "../../../lib/format";
import { t } from "../../../lib/sprache";
import {
  canCheckNow, DMARC_RECOMMENDATION, recordPurpose, recordStatusPill, senderDomainView, type SenderDomainStatus,
} from "./absenderdomain-model";

/**
 * Stand der eigenen Absenderdomain (0102) unter Einstellungen · Mailversand · Absender und Antworten: Pille mit Erklärung,
 * DNS-Einträge des Versanddienstes als Tabelle mit „Kopieren“, Knopf „Jetzt prüfen“ (Serverfunktion sender-domain). Die Domain
 * selbst trägt das Formular darüber ein; gespeichert wird sie mit den übrigen Mail-Einstellungen.
 */
export function AbsenderdomainStand({ orgId, domain, localPart, status, ready, records, checkedAt, error, locked }: {
  orgId: string;
  domain: string | null;
  localPart: string;
  status: SenderDomainStatus;
  ready: boolean;
  records: SenderDomainRecord[];
  checkedAt: string | null;
  error: string | null;
  locked: boolean;
}) {
  const { busy, run } = useAction();
  const address = domain ? `${localPart || "post"}@${domain}` : null;
  const view = senderDomainView(status, ready, address);
  const canCheck = canCheckNow(status, !!domain) && !locked;
  const available = typeof api.senderDomain === "function";
  const check = () => void run("pruefen", () => api.senderDomain!(orgId, "verify"), t("Stand beim Versanddienst abgefragt.", "Status requested from the email service."));
  return (
    <div className="stack" aria-live="polite">
      <p><PillView pill={view.pill} /></p>
      <p>{view.text}</p>
      {error && status !== "verified" && <p className="tk-error" role="alert">{error}</p>}
      {records.length > 0 && (
        <>
          <div className="tablewrap">
            <table>
              <caption className="sr-only">{t("DNS-Einträge für die eigene Absenderdomain", "DNS records for the custom sender domain")}</caption>
              <thead><tr>
                <th scope="col">{t("Zweck", "Purpose")}</th><th scope="col">{t("Typ", "Type")}</th><th scope="col">{t("Name", "Name")}</th>
                <th scope="col">{t("Wert", "Value")}</th><th scope="col">{t("Priorität", "Priority")}</th><th scope="col">{t("Stand", "Status")}</th>
              </tr></thead>
              <tbody>
                {records.map((r, i) => (
                  <tr key={`${r.type}-${r.name}-${i}`}>
                    <td>{recordPurpose(r)}</td>
                    <td><code>{r.type ?? "–"}</code></td>
                    <td><code className="tk-dns-wert">{r.name ?? "–"}</code>{r.name && <CopyButton value={r.name} label={t("Namen kopieren", "Copy name")} />}</td>
                    <td><code className="tk-dns-wert">{r.value ?? "–"}</code>{r.value && <CopyButton value={r.value} label={t("Wert kopieren", "Copy value")} />}</td>
                    <td>{r.priority ?? "–"}</td>
                    <td><PillView pill={recordStatusPill(r.status)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <small>{t(`Namen gelten relativ zu ${domain ?? "eurer Domain"}; manche Anbieter verlangen den vollen Namen (Name, Punkt, Domain). TTL: Standard des Anbieters.`,
            `Names are relative to ${domain ?? "your domain"}; some providers need the full name (name, dot, domain). TTL: the provider's default.`)}</small>
          <small>
            {t("Empfohlen, vom Versanddienst nicht geprüft: ein DMARC-Eintrag, falls es für die Domain noch keinen gibt: ", "Recommended, not checked by the email service: a DMARC record if the domain does not have one yet: ")}
            <code>{DMARC_RECOMMENDATION.type} {DMARC_RECOMMENDATION.name} “{DMARC_RECOMMENDATION.value}”</code>
          </small>
        </>
      )}
      {domain && (
        <div className="row">
          {canCheck && (
            <button type="button" onClick={check} disabled={!!busy || !available} aria-busy={busy === "pruefen"}
              aria-describedby={!available ? "absender-demo" : undefined}>
              {busy === "pruefen" ? t("Wird geprüft …", "Checking …") : t("Jetzt prüfen", "Check now")}
            </button>
          )}
          {checkedAt && <small>{t(`Zuletzt geprüft: ${date(checkedAt)}, ${time(checkedAt)}`, `Last checked: ${date(checkedAt)}, ${time(checkedAt)}`)}</small>}
          {!available && <small id="absender-demo">{t("Im Demo-Modus gibt es keinen Versanddienst; die Domain bleibt vorgemerkt.", "The demo has no email service; the domain stays scheduled.")}</small>}
        </div>
      )}
    </div>
  );
}

/** Wert in die Zwischenablage; ohne Zwischenablage (unsichere Verbindung) der Hinweis zum Markieren. */
function CopyButton({ value, label }: { value: string; label: string }) {
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      toast(t("Kopiert.", "Copied."));
    } catch {
      toast(t("Kopieren ging nicht. Bitte den Wert markieren und kopieren.", "Copying failed. Please select the value and copy it."), "alert");
    }
  }
  return <button type="button" className="tk-dns-kopieren" onClick={() => void copy()} aria-label={label}>{t("Kopieren", "Copy")}</button>;
}
